import React, { useMemo } from 'react';
import { useIO, useLinked } from "@type-r/react";
import { PdfViewer } from './pdf-viewer';
import { Button, div, PopupDialog, PrimaryButton } from 'lqx-react';
import { IconProps, makeStyles, styled } from '@material-ui/core';
import { DigiTooltip } from '../../shared';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ToolIcon from '@material-ui/icons/Tune';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import { CsvViewer } from './csv-viewer';
import { HtmlViewer } from './html-viewer';
import { JpgViewer } from './jpg-viewer';

export const FileViewer = ({ fileName, url, scale, downloadFile }: { fileName: string, url: string, scale?: number, downloadFile: any }) => {
    const classes = useFileViewerStyles();

    const $isFullScreen = useLinked(false);
    const $downloadIO = useLinked(false);
    const zoomStep = 0.4;
    const $zoom = useLinked(scale || 1);
    const $size = useLinked(false);
    const $loaded = useLinked(false);
    const fileType = useMemo(() => fileName.split('.').pop().toLowerCase(), [fileName]);

    const $zoomHtml = useLinked(1);

    useIO(async () => {
        try {
            return $downloadIO.isTruthy && await downloadFile(url, fileName);
        } finally {
            $downloadIO.set(false);
        }
    }, [$downloadIO.value]);

    const zoomInHtml = () => {
        $zoomHtml.set(prevZoom => Math.min(prevZoom + 0.1, 3)); // max zoom level 3
    };

    const zoomOutHtml = () => {
        $zoomHtml.set(prevZoom => Math.max(prevZoom - 0.1, 0.5)); // min zoom level 0.5
    };

    const zoomIn = () => $zoom.set($zoom.value + zoomStep);

    const zoomOut = () => $zoom.set($zoom.value - zoomStep > zoomStep ? $zoom.value - zoomStep : zoomStep);

    const sizeToggle = () => $size.set(!$size.value);

    return (
        <>
            {$loaded.value ?
                <Toolbar>
                    <span hidden={$size.value}>
                        <ToolbarButton qa-id="download" onClick={$downloadIO.true}><DownloadIcon /></ToolbarButton>
                        <ToolbarButton qa-id="zoom-in" onClick={fileType === 'msg' || fileType === 'tif' ? zoomInHtml : zoomIn}><ZoomInIcon /></ToolbarButton>
                        <ToolbarButton qa-id="zoom-out" onClick={fileType === 'msg' || fileType === 'tif' ? zoomOutHtml : zoomOut}><ZoomOutIcon /></ToolbarButton>
                        <ToolbarButton qa-id="full-screen" onClick={() => { $isFullScreen.set(true); $loaded.set(false) }}><FullscreenIcon /></ToolbarButton>
                    </span>
                    <ToolbarButton qa-id="hide" onClick={sizeToggle}>
                        {$size.value ? <ToolIcon /> : <ArrowBackIcon />}
                    </ToolbarButton>
                </Toolbar>
                : null}
            <DocumentContainer>
                <FileViewerResolver fileType={fileType} url={url} $scale={$zoom} $zoomLevel={$zoomHtml} $loaded={$loaded} />
            </DocumentContainer>

            {$isFullScreen.value ?
                <PopupDialog
                    className={classes.root}
                    title={
                        <>
                            <Title>
                                FULLSCREEN VIEW
                            </Title>
                        </>
                    }
                    fullScreen
                    actions={
                        <PrimaryButton onClick={$isFullScreen.false}>Close</PrimaryButton>
                    }>

                    <FullscreenToolbar>
                        <DigiTooltip title="DOWNLOAD ORIGINAL FILE">
                            <ToolbarButton qa-id="download" onClick={$downloadIO.true}><DownloadIcon /></ToolbarButton>
                        </DigiTooltip>
                        <DigiTooltip title="ZOOM IN">
                            <ToolbarButton qa-id="zoom-in" onClick={fileType === 'msg' || fileType === 'tif' ? zoomInHtml : zoomIn}><ZoomInIcon /></ToolbarButton>
                        </DigiTooltip>
                        <DigiTooltip title="ZOOM OUT">
                            <ToolbarButton qa-id="zoom-out" onClick={fileType === 'msg' || fileType === 'tif' ? zoomOutHtml : zoomOut}><ZoomOutIcon /></ToolbarButton>
                        </DigiTooltip>
                    </FullscreenToolbar>
                    <FullscreenDocumentContainer>
                        <FileViewerResolver fileType={fileType} url={url} $scale={$zoom} $zoomLevel={$zoomHtml} $loaded={$loaded} />
                    </FullscreenDocumentContainer>
                </PopupDialog>
                : null}
        </>
    );
}

const FileViewerResolver = ({ fileType, url, $scale, $zoomLevel, $loaded }: { fileType: string, url: string, $scale, $zoomLevel, $loaded }) => {
    return (
        <>
            {fileType === 'pdf' && <PdfViewer url={url} $scale={$scale} $loaded={$loaded} />}
            {fileType === 'csv' && <CsvViewer url={url} $scale={$scale} />}
            {fileType === 'msg' && <HtmlViewer url={`${url}?isHtmlFile=true`} $zoomLevel={$zoomLevel} $loaded={$loaded} />}
            {fileType === 'tif' && <JpgViewer url={`${url}?isJpgFile=true`} $zoomLevel={$zoomLevel} $loaded={$loaded} />}
        </>
    );
}

const FullscreenDocumentContainer = div({
    marginLeft: '-23px',
    height: 'calc(100% - 40px)',
    width: 'calc(100% + 47px)',
    overflow: 'auto',
    padding: 0,
    margin: 0
});

const DocumentContainer = div({
    minHeight: '100px',
    maxHeight: '900px',
    height: 'calc(100% - 34px)',
    overflow: 'auto',
    padding: 0,
    margin: 0
});

const InblockIcon = ({ ...rest }: Partial<IconProps>) => {
    const classes = useInblockIconStyles(rest);
    return <img className={classes.root} src="/assets/images/inblock_white.png" />
}

const useInblockIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginRight: '10px'
    }
});

const useFileViewerStyles = makeStyles({
    root: {
        '& .MuiDialogTitle-root': {
            margin: 0,
            padding: 0
        }
    }
});

const Title = div({
    backgroundColor: '#052C4C',
    position: 'relative',
    display: 'flex',
    padding: '0.5rem 1rem',
    minWidth: 'calc(100% + 14px)',
    color: 'white'
});

const ToolbarButton = styled(Button)({
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'white',
    padding: 0,
    margin: 0,
    '& .MuiSvgIcon-root': {
        fontSize: '24px',
    },
    '&:hover': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
    '&:active': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
    '&:focus': {
        boxShadow: '0 0 0 0',
        border: 'none',
    },
});

const FullscreenToolbar = div({
    border: '1px solid #E2F0F7',
    marginLeft: '-23px',
    marginTop: '-7px',
    width: 'calc(100% + 47px)',
});

const Toolbar = div({
    border: '1px solid #E2F0F7',
    background: 'white',
    position: 'absolute',
    height: '30',
    margin: '5px 0px 0px 5px',
    zIndex: 1
});
